import React, { useMemo } from 'react';
import styles from './ProductQuiz.module.scss';
import { Container, Flex, Text } from '@mantine/core';
import { useProductQuiz } from './ProductQuizContext';
import { ReactComponent as ArrowLeft } from '../../assets/images/icons/arrow_left.svg';
import { ReactComponent as Refresh } from '../../assets/images/icons/refresh.svg';
import { QuestionEmail } from './Questions/QuestionEmail';
import { useTranslation } from 'react-i18next';

const StepIndicator: React.FC = () => {
  const { recommendation, component, currentQuestionIndex } = useProductQuiz();

  const showStepIndicator = useMemo(() => {
    if (recommendation !== null) {
      return false;
    }
    if (React.isValidElement(component) && component.type === QuestionEmail) {
      return false;
    }

    return true;
  }, [recommendation, component]);

  return (
    <Flex
      w={37}
      h={37}
      bg={currentQuestionIndex !== 0 ? 'black' : 'white'}
      c={currentQuestionIndex !== 0 ? 'white' : 'black'}
      sx={{
        borderRadius: '50%',
        zIndex: 2,
      }}
      align="center"
      justify="center"
      fz={14}
      fw={600}
      opacity={showStepIndicator ? 1 : 0}
    >
      {currentQuestionIndex + 1}
    </Flex>
  );
};

export const ProductQuizBanner: React.FC<{ finished?: boolean }> = ({ finished = false }) => {
  const { t } = useTranslation();
  const { currentQuestionIndex, previousQuestion, resetQuiz } = useProductQuiz();
  if (currentQuestionIndex > 0 || finished) {
    const actionIcon = finished ? (
      <Refresh onClick={resetQuiz} className={styles.noHighlight} />
    ) : (
      <ArrowLeft onClick={previousQuestion} className={styles.noHighlight} />
    );
    return (
      <Container
        fluid
        sx={(theme) => ({
          color: theme.colors['gray-300'],
          background: theme.colors['background-dark-tertiary'],
          boxShadow: theme.shadows.md,
        })}
        px={0}
      >
        <Flex direction={'row'} w={'100%'} py={16} px={20} align="center" justify="space-between">
          {actionIcon}
          <Text
            className={styles.rightForMeText}
            align="left"
            lh={1.2}
            size={16}
            weight={600}
            sx={{ letterSpacing: '-0.16px' }}
          >
            {t('fi_product_quiz')}
          </Text>
          <StepIndicator />
        </Flex>
      </Container>
    );
  } else {
    return (
      <Flex
        className={styles.quizBannerStep}
        sx={(theme) => ({ color: 'black', background: theme.colors['fi-yellow'], boxShadow: theme.shadows.md })}
      >
        <Flex direction={'column'} py={24} style={{ paddingLeft: 20, marginLeft: 0 }} gap={2.5}>
          <Text
            className={styles.rightForMeText}
            lh={1.2}
            align="left"
            size={16}
            weight={600}
            sx={{ letterSpacing: '-0.16px' }}
          >
            {t('which_fi_is_right_for_me')}
          </Text>
          <Text className={styles.getStartedTitle} size={32} weight={700} sx={{ letterSpacing: '-0.96px' }}>
            {t('lets_get_started')}
          </Text>
        </Flex>
        <Flex ml={'auto'} pr={20} align="center">
          <StepIndicator />
        </Flex>
      </Flex>
    );
  }
};
